import { Checkbox } from '@ads/components-react';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import type { UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useParcelFormError } from '../../../hooks/useParcelFormError';
import type { ParcelFormValues } from '../../../hooks/useParcelFormSchema';
import type { ParcelFieldName } from '../../../types/parcelEdit';
import { isUnseededField } from '../../../types/parcelEdit';
import { deriveParcelChargeValue } from '../../../utils/deriveParcelChargeValue';
import { EMPTY_VALUE, formatCurrency } from '../../../utils/formatCurrency';
import { ParcelNumericField } from '../../molecules/ParcelNumericField';
import { PlanReadOnlyField } from '../../molecules/PlanReadOnlyField';
import { PlanSectionCard } from '../../molecules/PlanSectionCard';

/** Props de `ParcelValuesSection`. */
export interface ParcelValuesSectionProps {
  readonly form: UseFormReturn<ParcelFormValues>;
  /**
   * Campos que a fonte de leitura não consegue semear — exibidos desabilitados,
   * com a explicação da lacuna. Vem de `useParcelEditSeed`; a criação não passa.
   */
  readonly disabledFields?: readonly ParcelFieldName[] | undefined;
  /**
   * Renderiza fração de parcelas e as duas flags de reset. Só na edição: os três
   * campos operam sobre uma parcela que já existe.
   */
  readonly showEditOnlyFields?: boolean | undefined;
}

/**
 * Seção de valores da parcela, compartilhada pela criação e pela edição.
 *
 * O "Valor a cobrar" é derivado **apenas** do desconto total informado — ver
 * `deriveParcelChargeValue`. Pontualidade e retroativo têm fórmula do backend e
 * podem já estar contidos no total; somá-los mostraria um número menor que o real.
 *
 * A multa não entra no cálculo porque incide após o vencimento, não na criação — o
 * texto de apoio diz isso, para o operador não supor que já está cobrada.
 *
 * Na edição, os campos listados em `disabledFields` aparecem vazios e travados: a
 * grid do contrato não devolve o valor atual deles, e um campo editável partindo do
 * vazio faria o operador acreditar que o valor real é zero.
 */
export function ParcelValuesSection({
  form,
  disabledFields,
  showEditOnlyFields,
}: ParcelValuesSectionProps) {
  const { t } = useTranslation('individualPayment');
  const { control, watch } = form;
  const errorOf = useParcelFormError(form);

  const total = watch('total') as number | null | undefined;
  const totalDiscount = watch('totalDiscount') as number | null | undefined;
  const chargeValue = deriveParcelChargeValue(total, totalDiscount);

  /** Desabilitado por lacuna de leitura carrega sempre a mesma explicação. */
  const unseeded = (field: ParcelFieldName) => {
    const disabled = isUnseededField(disabledFields, field);
    return {
      disabled,
      ...(disabled ? { helper: t('parcelEdit.values.unseededHint') } : {}),
    };
  };

  return (
    <PlanSectionCard title={t('parcelCreate.values.title')} icon={CurrencyDollarIcon}>
      <div className="flex flex-wrap gap-xxxs">
        <div className="flex flex-1 basis-64">
          <ParcelNumericField
            control={control}
            name="total"
            kind="currency"
            required
            label={t('parcelCreate.values.total')}
            placeholder={t('parcelCreate.values.totalPlaceholder')}
            error={errorOf('total')}
            {...unseeded('total')}
          />
        </div>
        <div className="flex flex-1 basis-64">
          <ParcelNumericField
            control={control}
            name="discountPunctualPercentage"
            kind="percentage"
            label={t('parcelCreate.values.discountPunctual')}
            placeholder={t('parcelCreate.values.percentagePlaceholder')}
            error={errorOf('discountPunctualPercentage')}
            {...unseeded('discountPunctualPercentage')}
          />
        </div>
        <div className="flex flex-1 basis-64">
          <ParcelNumericField
            control={control}
            name="retroactiveDiscount"
            kind="percentage"
            label={t('parcelCreate.values.discountRetroactive')}
            placeholder={t('parcelCreate.values.percentagePlaceholder')}
            error={errorOf('retroactiveDiscount')}
            {...unseeded('retroactiveDiscount')}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-xxxs">
        <div className="flex flex-1 basis-64">
          <ParcelNumericField
            control={control}
            name="totalDiscount"
            kind="percentage"
            label={t('parcelCreate.values.discountTotal')}
            placeholder={t('parcelCreate.values.percentagePlaceholder')}
            error={errorOf('totalDiscount')}
            {...unseeded('totalDiscount')}
          />
        </div>
        <div className="flex flex-1 basis-64">
          <ParcelNumericField
            control={control}
            name="fine"
            kind="percentage"
            label={t('parcelCreate.values.fine')}
            placeholder={t('parcelCreate.values.percentagePlaceholder')}
            error={errorOf('fine')}
            {...unseeded('fine')}
          />
        </div>
        {showEditOnlyFields === true ? (
          <div className="flex flex-1 basis-64">
            <ParcelNumericField
              control={control}
              name="fractionOfParcel"
              kind="integer"
              label={t('parcelEdit.values.fractionOfParcel')}
              placeholder={t('parcelCreate.values.percentagePlaceholder')}
              info={t('parcelEdit.values.fractionOfParcelInfo')}
              error={errorOf('fractionOfParcel')}
              {...unseeded('fractionOfParcel')}
            />
          </div>
        ) : null}
        <div className="flex flex-1 basis-64">
          <PlanReadOnlyField label={t('parcelCreate.values.chargeValue')}>
            <span className="ts-heading-xs text-ctx-content-title">
              {chargeValue === null ? EMPTY_VALUE : formatCurrency(chargeValue)}
            </span>
          </PlanReadOnlyField>
        </div>
      </div>

      {showEditOnlyFields === true ? (
        <div className="flex flex-wrap gap-xxxs">
          <ParcelResetFlag
            form={form}
            name="resetExtendedExpiration"
            label={t('parcelEdit.values.resetExtendedExpiration')}
          />
          <ParcelResetFlag
            form={form}
            name="removePunctualDiscount"
            label={t('parcelEdit.values.removePunctualDiscount')}
          />
          <div className="flex flex-1 basis-64" />
        </div>
      ) : null}
    </PlanSectionCard>
  );
}

/**
 * Interruptor de uma das duas flags de reset da edição.
 *
 * As duas não são valores da parcela, e sim ordens ao backend — por isso não têm
 * semeadura nem lacuna: partem sempre de desligadas.
 */
function ParcelResetFlag({
  form,
  name,
  label,
}: {
  readonly form: UseFormReturn<ParcelFormValues>;
  readonly name: 'resetExtendedExpiration' | 'removePunctualDiscount';
  readonly label: string;
}) {
  return (
    <div className="flex flex-1 basis-64 flex-col gap-quark">
      <Controller
        control={form.control}
        name={name}
        render={({ field }) => (
          <Checkbox
            label={label}
            size="md"
            checked={field.value === true}
            onCheckedChange={checked => field.onChange(checked === true)}
          />
        )}
      />
    </div>
  );
}
